import { useEffect } from 'react';

import { useActorRef } from '@xstate/react';
import { type ActorRefFrom } from 'xstate';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { adjustInitializationMachine } from '../../machine/adjust-initialization-machine';
import {
  hasOptedInToOneTrust,
  handleAdjustUnmount,
  subscribeToOneTrustConsentChanged,
  getAdjustInitializer,
} from '../../utils';

// ─────────────────────────────────────────────────────────────────────────────

export const useAdjustInitialization = (
  userId?: string | null,
): ActorRefFrom<typeof adjustInitializationMachine> => {
  const isAdjustEnabled = useFeatureFlag('permanent-adjust-enabled');
  const { track } = useTelemetry();

  const actorRef = useActorRef(
    adjustInitializationMachine.provide({
      guards: {
        canTrack: () => isAdjustEnabled && hasOptedInToOneTrust(),
      },
      actions: {
        initialize: () => getAdjustInitializer(track),
      },
    }),
  );

  useEffect(() => {
    actorRef.send({ type: 'SYNC_CUSTOMER', userId });
  }, [actorRef, userId, isAdjustEnabled]);

  useEffect(() => {
    return subscribeToOneTrustConsentChanged(() => {
      actorRef.send({ type: 'SYNC_CUSTOMER', userId });
    });
  }, [actorRef, userId]);

  useEffect(() => {
    return () => {
      handleAdjustUnmount();
    };
  }, []);

  return actorRef;
};
